/**
 * Estimates the HTML the backend stores for an editor value. The backend sanitizes saved HTML (the output matches
 * the OWASP Java HTML Sanitizer): it encodes = + @ ` " ' in text and attributes as numeric entities, respaces inline
 * CSS ("color: rgb(0, 102, 204);" becomes "color:rgb( 0 , 102 , 204 )") and writes void elements as "<br />".
 * The stored value is cut off at its column length, so length limits must be checked against this, not the editor HTML.
 * Where the sanitizer's behavior isn't known, the estimate errs on the longer side.
 */

const ENTITIES: Record<string, string> = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&#34;', '\'': '&#39;', '+': '&#43;', '=': '&#61;', '@': '&#64;', '`': '&#96;'
};

const VOID_ELEMENTS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'source', 'track', 'wbr']);

const encode = (value: string): string => value.replace(/[&<>"'+=@`]/g, c => ENTITIES[c]);

const normalizeCss = (css: string): string => css.split(';')
  .map(declaration => declaration.trim())
  .filter(declaration => declaration.includes(':'))
  .map(declaration => {
    const separator = declaration.indexOf(':');
    const value = declaration.slice(separator + 1)
      .replace(/\s*\(\s*/g, '( ')
      .replace(/\s*\)/g, ' )')
      .replace(/\s*,\s*/g, ' , ')
      .replace(/\s+/g, ' ')
      .trim();
    return declaration.slice(0, separator).trim().toLowerCase() + ':' + value;
  })
  .join(';');

const serialize = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    return encode((node as Text).data);
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }
  const element = node as Element;
  const tag = element.tagName.toLowerCase();
  const attributes = Array.from(element.attributes)
    .map(attribute => ` ${attribute.name}="${encode(attribute.name === 'style' ? normalizeCss(attribute.value) : attribute.value)}"`)
    .join('');
  if (VOID_ELEMENTS.has(tag)) {
    return `<${tag}${attributes} />`;
  }
  return `<${tag}${attributes}>${Array.from(element.childNodes).map(serialize).join('')}</${tag}>`;
};

let lastHtml: string | null = null;
let lastStoredHtml = '';

/**
 * The HTML the backend is expected to store for the given editor HTML.
 */
export function estimateStoredHtml(html: string | null | undefined): string {
  if (!html) {
    return '';
  }
  if (html !== lastHtml) {
    const body = new DOMParser().parseFromString(html, 'text/html').body;
    lastStoredHtml = Array.from(body.childNodes).map(serialize).join('');
    lastHtml = html;
  }
  return lastStoredHtml;
}
